import { useState } from "react"
import {
  usePresignUploadMutation,
  useUploadCSVMutation,
  type ValidationError,
} from "~/ui-rtk/api/ingestionApi"
import FileUpload from "~/ui-rtk/components/ui/controls/FileUpload"
import { Button } from "~/ui-rtk/components/ui/controls"
import { toast } from "react-toastify"
import ValidationErrorDialog from "../ValidationErrorDialog"
import { uploadToPresignedUrl } from "../utils"
import {
  MAX_PRESIGNED_UPLOAD_MB,
  PRESIGN_THRESHOLD_BYTES,
  PRESIGN_THRESHOLD_MB,
} from "~/shared/ingestion/upload-limits"

type CSVUploadFormProps = {
  companyConnectorId: string
  /** Per-report key under Connector.metadata.ingestionConfig.reports. */
  reportType: string
  onSuccess?: (response: { ingestionHistoryId: string; status: string }) => void
  onError?: (error: unknown) => void
}

interface ValidationErrorResponse {
  message: string
  errors: ValidationError[]
  ingestionHistoryId?: string
}

const describeError = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message
  return error && typeof error === "object" && "data" in error
    ? JSON.stringify(error.data)
    : fallback
}

export default function CSVUploadForm({
  companyConnectorId,
  reportType,
  onSuccess,
  onError,
}: CSVUploadFormProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadCSV, { isLoading }] = useUploadCSVMutation()
  const [presignUpload, { isLoading: isPresigning }] =
    usePresignUploadMutation()
  // Fraction of a presigned PUT sent so far; null when no PUT is in flight.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )
  const [errorIngestionHistoryId, setErrorIngestionHistoryId] = useState<
    string | undefined
  >()
  const [showErrorDialog, setShowErrorDialog] = useState(false)

  const isUploading = isLoading || isPresigning || uploadProgress !== null

  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      setSelectedFile(files[0])
      // Clear previous errors when selecting a new file
      setValidationErrors([])
      setShowErrorDialog(false)
    } else {
      setSelectedFile(null)
    }
  }

  /**
   * Large-file path (APP-537): the API signs a PUT and the bytes go straight
   * to S3. Nothing is validated before they land — the processor validates
   * asynchronously and the outcome shows up in the history as COMPLETED or
   * FAILED — which is why this is only used above PRESIGN_THRESHOLD_BYTES.
   */
  const uploadViaPresign = async (file: File) => {
    try {
      const presigned = await presignUpload({
        companyConnectorId,
        reportType,
        fileName: file.name,
        size: file.size,
        contentType: file.type || undefined,
      }).unwrap()

      setUploadProgress(0)
      await uploadToPresignedUrl(
        presigned.uploadUrl,
        file,
        presigned.requiredHeaders,
        setUploadProgress,
      )

      toast.success(
        "Upload complete. The file is being validated and processed — check the history below for the result.",
      )
      setSelectedFile(null)
      onSuccess?.({
        ingestionHistoryId: presigned.ingestionHistoryId,
        status: "PENDING",
      })
    } catch (error) {
      toast.error(describeError(error, "Failed to upload CSV file"))
      onError?.(error)
    } finally {
      setUploadProgress(null)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload")
      return
    }

    // Route by size. Small files keep the multipart path and its synchronous,
    // precise validation errors (deliberate per APP-376); above the threshold
    // the body would not reach the API anyway, so it is presigned instead.
    if (selectedFile.size > PRESIGN_THRESHOLD_BYTES) {
      await uploadViaPresign(selectedFile)
      return
    }

    try {
      const result = await uploadCSV({
        file: selectedFile,
        companyConnectorId,
        reportType,
      }).unwrap()

      toast.success(result.message || "CSV uploaded successfully")
      setSelectedFile(null)
      setValidationErrors([])
      setShowErrorDialog(false)
      onSuccess?.(result)
    } catch (error) {
      // Check if this is a validation error response
      if (
        error &&
        typeof error === "object" &&
        "data" in error &&
        error.data &&
        typeof error.data === "object"
      ) {
        const errorData = error.data as ValidationErrorResponse

        // Check if it has validation errors array
        if (
          errorData.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          setValidationErrors(errorData.errors)
          setErrorIngestionHistoryId(errorData.ingestionHistoryId)
          setShowErrorDialog(true)
          onError?.(error)
          return
        }
      }

      // Fallback to toast for other errors
      toast.error(describeError(error, "Failed to upload CSV file"))
      onError?.(error)
    }
  }

  let buttonLabel = "Upload CSV"
  if (uploadProgress !== null) {
    buttonLabel = `Uploading… ${Math.round(uploadProgress * 100)}%`
  } else if (isUploading) {
    buttonLabel = "Uploading..."
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Upload CSV File
        </h3>
        <p className="text-sm text-platinum mb-4">
          Select a CSV file to upload. Small files are checked before they are
          accepted; files over {PRESIGN_THRESHOLD_MB}MB are checked after
          upload, with the result in the history below. Large exports can be
          gzipped — a <code>.csv.gz</code> is decompressed on our side, which is
          how a multi-gigabyte export fits under the {MAX_PRESIGNED_UPLOAD_MB}MB
          upload limit.
        </p>
      </div>

      <FileUpload
        onUpload={handleFileSelect}
        accept=".csv,.gz,.csv.gz,text/csv,application/gzip"
        maxSize={MAX_PRESIGNED_UPLOAD_MB}
        maxFiles={1}
        isMultiple={false}
        text="Select CSV File"
        label="CSV File"
      />

      {selectedFile && (
        <div className="p-3 bg-basic-dark-blue/50 border border-basic-blue rounded-lg">
          <p className="text-sm text-white">
            Selected: <span className="font-medium">{selectedFile.name}</span>
          </p>
          <p className="text-xs text-platinum mt-1">
            Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
          </p>
        </div>
      )}

      <Button
        variant={{ variant: "solid", color: "white" }}
        onClick={handleUpload}
        disabled={!selectedFile || isUploading}
        isLoading={isUploading}
        className="w-full"
      >
        {buttonLabel}
      </Button>

      <ValidationErrorDialog
        isOpen={showErrorDialog}
        onClose={() => {
          setShowErrorDialog(false)
          setValidationErrors([])
        }}
        errors={validationErrors}
        ingestionHistoryId={errorIngestionHistoryId}
      />
    </div>
  )
}
